import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';

import { listWorkspaceFiles } from '@/lib/api/generated/api';
import type { FileListing } from '@/lib/api/generated/model';
import { parentFolderOf } from '../filePreviewLink';
import { isFolderPlaceholder } from '../folders';
import { fileKeys } from '../queryKeys';

/**
 * Whether the workspace has each of some paths (spec 095 FR-018, FR-019).
 *
 * One listing per distinct parent folder, never one request per path, and
 * under the file browser's own query keys — so a folder the browser has
 * already listed costs nothing here, and a folder listed here is warm for the
 * browser. The answer is three-valued: `true` when the folder's listing holds
 * the path, `false` when it does not or the folder itself does not exist
 * (`422`), `undefined` while the listing is still out or could not be read.
 * Callers render prose at once and link when `true` arrives (FR-021).
 */
export function useWorkspaceFileExistence(
  workspaceId: string,
  paths: readonly string[],
): { exists: (path: string) => boolean | undefined } {
  const folders = useMemo(
    () => [...new Set(paths.map(parentFolderOf))],
    [paths],
  );

  const read = (folder: string) => async (): Promise<FileListing | null> => {
    const response = await listWorkspaceFiles(workspaceId, { path: folder });
    // A folder that is not there is an answer — "no such file" — not a
    // failure: 422 for a malformed path, 404 for a missing one.
    if (response.status === 422 || response.status === 404) return null;
    if (response.status !== 200) {
      throw new Error(`Could not read “${folder}”.`);
    }
    return {
      ...response.data,
      entries: response.data.entries.filter(
        (entry) => !isFolderPlaceholder(entry),
      ),
    };
  };

  /*
    The root first, and a deeper folder only when the root holds its first
    segment (spec 127 FR-014). Prose names repositories (`owner/repo`), API
    paths (`api/v4`) and branches as often as files; reading each as a folder
    cost a failed request apiece — thirteen on one drawer. The root listing is
    the same cached read the file browser makes, so this costs one request.
  */
  const needsRoot = folders.length > 0;
  const root = useQuery({
    queryKey: fileKeys.listing(workspaceId, ''),
    queryFn: read(''),
    retry: false,
    enabled: needsRoot,
  });
  const rootFolders = useMemo(() => {
    if (root.isPending || root.isError) return undefined;
    if (root.data === null || root.data === undefined) return new Set<string>();
    return new Set(
      root.data.entries
        .filter((entry) => entry.kind === 'directory')
        .map((entry) => entry.path),
    );
  }, [root]);

  const deeper = useMemo(
    () => folders.filter((folder) => folder !== ''),
    [folders],
  );
  const listings = useQueries({
    queries: deeper.map((folder) => ({
      queryKey: fileKeys.listing(workspaceId, folder),
      queryFn: read(folder),
      retry: false,
      enabled: rootFolders?.has(folder.split('/')[0] ?? '') ?? false,
    })),
  });

  const known = useMemo(() => {
    const byFolder = new Map<string, Set<string> | null | undefined>();
    const filesOf = (listing: FileListing | null | undefined) =>
      listing
        ? new Set(
            listing.entries
              .filter((entry) => entry.kind === 'file')
              .map((entry) => entry.path),
          )
        : null;
    if (needsRoot) {
      byFolder.set(
        '',
        rootFolders === undefined ? undefined : filesOf(root.data),
      );
    }
    deeper.forEach((folder, index) => {
      if (rootFolders === undefined) {
        byFolder.set(folder, undefined);
        return;
      }
      // Not under any folder the workspace has: not a file, and never asked.
      if (!rootFolders.has(folder.split('/')[0] ?? '')) {
        byFolder.set(folder, null);
        return;
      }
      const query = listings[index];
      if (!query || query.isPending || query.isError) {
        byFolder.set(folder, undefined);
      } else {
        byFolder.set(folder, filesOf(query.data));
      }
    });
    return byFolder;
  }, [deeper, listings, needsRoot, root, rootFolders]);

  return {
    exists: (path) => {
      const folder = known.get(parentFolderOf(path));
      if (folder === undefined) return undefined;
      if (folder === null) return false;
      return folder.has(path);
    },
  };
}
